import { normalizeContractLinkUrl } from 'src/app/shared/utils/url.util';

export { normalizeContractLinkUrl };

export const QUOTE_TERMS_CONDITIONS_ELEMENT_NAME = 'Terms and Conditions';

export interface QuoteTermsContractLink {
  enabled: boolean;
  linkText: string;
  url: string;
}

export interface QuoteTermItem {
  id: string;
  name: string;
  selected?: boolean;
  required?: boolean;
  contractLink?: QuoteTermsContractLink;
}

export interface QuoteTermsAcceptContext {
  elementId: string;
  term: QuoteTermItem;
  contractLink: QuoteTermsContractLink;
}

export const DEFAULT_QUOTE_TERM_CONTRACT_LINK: QuoteTermsContractLink = {
  enabled: false,
  linkText: '',
  url: '',
};

export const DEFAULT_QUOTE_TERMS_LABEL = 'Terms and Conditions';

export function createQuoteTermId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `term_${crypto.randomUUID()}`;
  }
  return `term_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function defaultQuoteTermContractLink(): QuoteTermsContractLink {
  return { ...DEFAULT_QUOTE_TERM_CONTRACT_LINK };
}

export function normalizeQuoteTermContractLink(
  raw?: Partial<QuoteTermsContractLink> | null
): QuoteTermsContractLink {
  return {
    enabled: !!raw?.enabled,
    linkText: String(raw?.linkText || '').trim(),
    url: String(raw?.url || '').trim(),
  };
}

/** Fresh nested state for each dropped Terms and Conditions element (avoids shared references). */
export function cloneQuoteTermsElementForDrop(proto: any): any {
  return {
    ...proto,
    col: 'one',
    label: proto?.label || DEFAULT_QUOTE_TERMS_LABEL,
    custom_value: { type: true, value: [] as QuoteTermItem[] },
  };
}

function migrateLegacyElementContractLink(item: any): QuoteTermsContractLink | null {
  const legacy = item?.terms_config?.contractLink;
  if (!legacy?.enabled && !legacy?.linkText) {
    return null;
  }
  return normalizeQuoteTermContractLink(legacy);
}

export function ensureQuoteTermsElementShape(item: any): void {
  if (!item || item.name !== QUOTE_TERMS_CONDITIONS_ELEMENT_NAME) {
    return;
  }
  if (!item.custom_value) {
    item.custom_value = { type: true, value: [] };
  } else if (!Array.isArray(item.custom_value.value)) {
    item.custom_value.value = [];
  }
  const legacyLink = migrateLegacyElementContractLink(item);
  item.custom_value.value = item.custom_value.value
    .filter((row: any) => row && String(row.name || '').trim())
    .map((row: any) => {
      const contractLink =
        row.contractLink?.enabled || row.contractLink?.linkText
          ? normalizeQuoteTermContractLink(row.contractLink)
          : legacyLink || defaultQuoteTermContractLink();
      if (contractLink.url) {
        contractLink.url = normalizeContractLinkUrl(contractLink.url);
      }
      return {
        id: row.id || createQuoteTermId(),
        name: String(row.name).trim(),
        selected: !!row.selected,
        required: row.required !== false,
        contractLink,
      };
    });
  if (item.terms_config) {
    delete item.terms_config;
  }
}

export function getQuoteTermsCheckboxSizePx(fontSize: string | number | null | undefined): number {
  const size = Number(fontSize) || 14;
  return Math.max(12, Math.round(size * 1.3));
}

export function getQuoteTermsCheckmarkSizePx(fontSize: string | number | null | undefined): number {
  const size = Number(fontSize) || 14;
  return Math.max(9, Math.round(size * 0.85));
}

export function getQuoteTermsCheckboxGlyph(
  term: Pick<QuoteTermItem, 'selected'> | null | undefined
): string {
  return term?.selected ? '\u2611' : '\u2610';
}

/** Parse field_styles whether stored as array, JSON string, or double-encoded JSON string. */
export function parseFieldStylesJson(raw: unknown): any[] | null {
  if (!raw) {
    return null;
  }
  if (Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw !== 'string') {
    return null;
  }
  try {
    let parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function normalizeTermNameKey(name: string | null | undefined): string {
  return String(name || '').trim().toLowerCase();
}

/** Merge term.selected from listing field_styles into the display snapshot (e.g. PDF payloads). */
export function mergeQuoteTermsSelectionFromListing(
  targetFieldStyles: any[] | string | null | undefined,
  sourceFieldStyles: any[] | string | null | undefined
): any[] {
  const target = parseFieldStylesJson(targetFieldStyles);
  const source = parseFieldStylesJson(sourceFieldStyles);
  if (!target?.length || !source?.length) {
    return target || [];
  }

  const sourceTermsElements = findQuoteTermsElements(source);
  if (!sourceTermsElements.length) {
    return target;
  }

  const selectedByTermId = new Map<string, boolean>();
  const selectedByTermName = new Map<string, boolean>();
  for (const element of sourceTermsElements) {
    for (const term of element.custom_value?.value || []) {
      if (term?.id) {
        selectedByTermId.set(term.id, !!term.selected);
      }
      const nameKey = normalizeTermNameKey(term?.name);
      if (nameKey) {
        selectedByTermName.set(nameKey, !!term.selected);
      }
    }
  }

  const sourceTermsByElementId = new Map<string, QuoteTermItem[]>();
  for (const element of sourceTermsElements) {
    if (element?.id) {
      sourceTermsByElementId.set(
        element.id,
        element.custom_value?.value || []
      );
    }
  }
  const fallbackSourceTerms = sourceTermsElements[0]?.custom_value?.value || [];

  return target.map((page) => ({
    ...page,
    items: (page?.items || []).map((item: any) => {
      if (item?.name !== QUOTE_TERMS_CONDITIONS_ELEMENT_NAME) {
        return item;
      }
      const sourceTerms =
        (item?.id && sourceTermsByElementId.get(item.id)) || fallbackSourceTerms;
      const terms = (item.custom_value?.value || []).filter(
        (row: any) => row && String(row.name || '').trim()
      );
      return {
        ...item,
        custom_value: {
          ...item.custom_value,
          value: terms.map((term: any, index: number) => {
            const nameKey = normalizeTermNameKey(term?.name);
            const existingId = term?.id ? String(term.id) : '';
            let selected = !!term.selected;
            if (existingId && selectedByTermId.has(existingId)) {
              selected = !!selectedByTermId.get(existingId);
            } else if (nameKey && selectedByTermName.has(nameKey)) {
              selected = !!selectedByTermName.get(nameKey);
            } else if (sourceTerms[index] != null) {
              selected = !!sourceTerms[index].selected;
            }
            const contractLink =
              term.contractLink?.enabled || term.contractLink?.linkText
                ? normalizeQuoteTermContractLink(term.contractLink)
                : defaultQuoteTermContractLink();
            if (contractLink.url) {
              contractLink.url = normalizeContractLinkUrl(contractLink.url);
            }
            return {
              id: existingId || sourceTerms[index]?.id || createQuoteTermId(),
              name: String(term.name).trim(),
              selected,
              required: term.required !== false,
              contractLink,
            };
          }),
        },
      };
    }),
  }));
}

/** Read field_styles from a quotesListing/viewListing response. */
export function extractListingFieldStyles(
  listingResponse: any
): any[] | string | null {
  const raw =
    listingResponse?.data?.field_styles ??
    listingResponse?.field_styles ??
    listingResponse?.data?.[0]?.field_styles ??
    null;
  return raw ?? null;
}

export function isQuoteListingApiSuccess(response: any): boolean {
  if (!response || response instanceof Error) {
    return false;
  }
  if (typeof response.status === 'number' && response.status >= 400) {
    return false;
  }
  if (response?.statusCode >= 400 || response?.error) {
    return false;
  }
  return !!(response?.data || response?.status === 200 || response?.statusCode === 200);
}

export function findQuoteTermsElements(fieldStyles: any[] | null | undefined): any[] {
  if (!Array.isArray(fieldStyles)) {
    return [];
  }
  const elements: any[] = [];
  for (const page of fieldStyles) {
    for (const item of page?.items || []) {
      if (item?.name === QUOTE_TERMS_CONDITIONS_ELEMENT_NAME) {
        ensureQuoteTermsElementShape(item);
        elements.push(item);
      }
    }
  }
  return elements;
}

export function hasActiveQuoteTerms(fieldStyles: any[] | null | undefined): boolean {
  const elements = findQuoteTermsElements(fieldStyles);
  return elements.some((el) => (el.custom_value?.value?.length ?? 0) > 0);
}

export function flattenQuoteTermsForAccept(
  fieldStyles: any[] | null | undefined
): QuoteTermsAcceptContext[] {
  const rows: QuoteTermsAcceptContext[] = [];
  for (const element of findQuoteTermsElements(fieldStyles)) {
    for (const term of element.custom_value?.value || []) {
      rows.push({
        elementId: element.id,
        term,
        contractLink: normalizeQuoteTermContractLink(term.contractLink),
      });
    }
  }
  return rows;
}

export function applyQuoteTermsAcceptance(
  fieldStyles: any[] | null | undefined,
  checkedByTermId: Record<string, boolean> | null | undefined
): any[] {
  if (!Array.isArray(fieldStyles)) {
    return [];
  }
  const checked = checkedByTermId || {};
  const checkedByName = new Map<string, boolean>();
  for (const row of flattenQuoteTermsForAccept(fieldStyles)) {
    const nameKey = normalizeTermNameKey(row.term.name);
    if (nameKey) {
      checkedByName.set(nameKey, !!checked[row.term.id]);
    }
  }
  return fieldStyles.map((page) => ({
    ...page,
    items: (page?.items || []).map((item: any) => {
      if (item?.name !== QUOTE_TERMS_CONDITIONS_ELEMENT_NAME) {
        return item;
      }
      const clone = {
        ...item,
        custom_value: { ...item.custom_value, value: [...(item.custom_value?.value || [])] },
      };
      clone.custom_value.value = clone.custom_value.value.map((term: QuoteTermItem) => {
        const id = term.id || createQuoteTermId();
        const nameKey = normalizeTermNameKey(term?.name);
        let selected = false;
        if (Object.prototype.hasOwnProperty.call(checked, id)) {
          selected = !!checked[id];
        } else if (nameKey && checkedByName.has(nameKey)) {
          selected = !!checkedByName.get(nameKey);
        }
        return {
          ...term,
          id,
          name: String(term?.name || '').trim(),
          selected,
          required: term.required !== false,
          contractLink: normalizeQuoteTermContractLink(term.contractLink),
        };
      });
      return clone;
    }),
  }));
}

export function isQuoteTermsAcceptanceComplete(
  fieldStyles: any[] | null | undefined,
  checkedByTermId: Record<string, boolean> | null | undefined
): boolean {
  const checked = checkedByTermId || {};
  const requiredTerms = flattenQuoteTermsForAccept(fieldStyles).filter((row) => row.term.required !== false);
  if (!requiredTerms.length) {
    return true;
  }
  return requiredTerms.every((row) => !!checked[row.term.id]);
}
